/**
 * Theme and User Preferences Management - Single Source of Truth
 *
 * FLOW:
 * 1. App loads → useUserTheme() fetches from API → setTheme() and setFavorites() update Redux
 * 2. User changes theme → setTheme() (instant UI) + updateThemeAsync() (background API sync)
 * 3. User adds/removes favorites → addFavorite()/removeFavorite() (instant UI) + updateFavoritesAsync() (background API sync)
 * 4. Layout reads Redux mode → syncs Pepsico DS theme
 *
 * ACTIONS:
 * - setTheme: Immediately update Redux theme (instant UI feedback)
 * - updateThemeAsync: Sync theme to API in background
 * - setFavorites: Set all favorites at once (from API)
 * - addFavorite: Add a single favorite (instant UI feedback)
 * - removeFavorite: Remove a single favorite (instant UI feedback)
 * - updateFavoritesAsync: Sync favorites to API in background
 */

import type {PayloadAction} from '@reduxjs/toolkit'
import {createSlice, createAsyncThunk} from '@reduxjs/toolkit'
import {updateUserTheme, getCurrentUserEmail, type FavoriteItem} from '@/utils/userHelper'

type ThemeMode = 'light' | 'dark'

interface ThemeState {
  mode: ThemeMode
  favorites: FavoriteItem[]
  loading: boolean
  error: string | null
}

const getInitialTheme = (): ThemeMode => {
  const storedTheme = localStorage.getItem('theme')
  return storedTheme === 'dark' ? 'dark' : 'light'
}

const initialState: ThemeState = {
  mode: getInitialTheme(),
  favorites: [],
  loading: false,
  error: null,
}

// Async thunk for updating theme via API
export const updateThemeAsync = createAsyncThunk(
  'theme/updateThemeAsync',
  async (theme: ThemeMode, {rejectWithValue}) => {
    try {
      const userEmail = getCurrentUserEmail()
      if (!userEmail) {
        throw new Error('User email not found')
      }

      const result = await updateUserTheme({
        username: userEmail,
        theme,
      })

      if (!result) {
        throw new Error('Failed to update theme')
      }

      return theme
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error')
    }
  },
)

// Note: We'll handle favorites add/remove in the reducers directly
// and sync with API in the background. No async thunk needed since
// we want instant UI feedback and don't need to wait for API response.

const themeSlice = createSlice({
  name: 'theme',
  initialState,
  reducers: {
    setTheme(state, action: PayloadAction<ThemeMode>) {
      state.mode = action.payload
      localStorage.setItem('theme', state.mode)
    },
    setFavorites(state, action: PayloadAction<FavoriteItem[]>) {
      state.favorites = action.payload
    },
    addFavorite(state, action: PayloadAction<FavoriteItem>) {
      // Check if already exists to avoid duplicates
      const exists = state.favorites.some(fav => fav.id === action.payload.id && fav.type === action.payload.type)
      if (!exists) {
        // Add to the beginning of the array so it appears on the first page
        state.favorites.unshift(action.payload)
      }
    },
    removeFavorite(state, action: PayloadAction<{id: string; type: string}>) {
      state.favorites = state.favorites.filter(
        fav => !(fav.id === action.payload.id && fav.type === action.payload.type),
      )
    },
  },
  extraReducers: builder => {
    builder
      // Theme async thunk cases
      .addCase(updateThemeAsync.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(updateThemeAsync.fulfilled, (state, action) => {
        state.loading = false
        state.mode = action.payload
        localStorage.setItem('theme', state.mode)
      })
      .addCase(updateThemeAsync.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
  },
})

export const {setTheme, setFavorites, addFavorite, removeFavorite} = themeSlice.actions
export const themeReducer = themeSlice.reducer
export type {ThemeMode, ThemeState}
export default themeReducer
